import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  computed,
  effect,
  forwardRef,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ControlValueAccessor,
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputMaskModule } from 'primeng/inputmask';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { Observable, catchError, map, of } from 'rxjs';
import {
  GetLoanSensitiveEinGQL,
  GetLoanSensitiveEinQuery,
  GetLoanSensitiveSsnGQL,
  GetLoanSensitiveSsnQuery,
} from 'src/app/graphql/generated';
import { PERMISSIONS } from '@constants/permissions.constants';
import { PermissionService } from '@guards/permission/service/permission.service';
import { ToastMessageService } from '@services/toast-message.service';
import { UtilsService } from '@services/utils/utils.service';

export enum MaskType {
  SSN = 'SSN',
  EIN = 'EIN',
}

@Component({
  selector: 'app-agility-toggle-mask',
  templateUrl: './agility-toggle-mask.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    InputMaskModule,
    InputTextModule,
    IconFieldModule,
    InputIconModule,
    TooltipModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AgilityToggleMaskComponent),
      multi: true,
    },
  ],
})
export class AgilityToggleMaskComponent
  implements OnInit, ControlValueAccessor
{
  readonly id = input.required<string>();
  readonly cssClass = input.required<string>();
  readonly styleClass = input<string>('');
  readonly borrowerId = input<string>('');
  readonly maskType = input.required<MaskType>();
  readonly fortaLoanId = input<string>();
  readonly sensitiveEntityId = input<string>();
  readonly sensitiveGuarantorIndex = input<number>();

  form: FormGroup = new FormGroup({ control: new FormControl('') });

  readonly focusToEdit = signal(false);
  readonly isReadOnly = signal(false);
  readonly isFetching = signal(false);
  readonly revealed = signal(false);
  readonly maskPlaceholder = computed(() =>
    this.maskType() === MaskType.SSN ? '999-99-9999' : '99-9999999'
  );
  readonly viewSensitiveLoanData = this.permissionService.hasPermission({
    permission: PERMISSIONS.viewSensitiveLoanData,
  });
  readonly toggleIconStyleClass = computed(() => {
    if (this.isFetching()) {
      return 'pi-spin pi-spinner';
    }
    return this.isReadOnly() ? 'pi-eye' : 'pi-eye-slash';
  });

  constructor(
    public readonly permissionService: PermissionService,
    private readonly utilsService: UtilsService,
    private readonly toastMessageService: ToastMessageService,
    private readonly getLoanSensitiveSsnGQL: GetLoanSensitiveSsnGQL,
    private readonly getLoanSensitiveEinGQL: GetLoanSensitiveEinGQL,
    private readonly destroyRef: DestroyRef,
    private readonly changeDetectorRef: ChangeDetectorRef
  ) {
    effect(() => {
      this.fortaLoanId();
      this.sensitiveEntityId();
      this.sensitiveGuarantorIndex();
      this.revealed.set(false);
    });
  }

  ngOnInit(): void {
    this.form
      .get('control')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        if (value && this.isValid() && this.form.get('control')?.dirty) {
          this.revealed.set(true);
        }
        this.onChange(value);
        this.onTouched();
      });
  }

  onChange: (value: string) => void = () => {};

  onTouched: () => void = () => {};

  writeValue(value: string): void {
    this.revealed.set(false);
    this.form.get('control')?.setValue(value || '', { emitEvent: false });
    this.form.get('control')?.markAsPristine();
    if (value) {
      this.toggleState();
    } else {
      this.isReadOnly.set(false);
    }
    this.changeDetectorRef.markForCheck();
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.form.disable();
    if (isDisabled) {
      this.form.get('control')?.disable();
    } else {
      this.form.get('control')?.enable();
    }
    this.changeDetectorRef.markForCheck();
  }

  getMaskPattern(): RegExp {
    if (this.maskType() === MaskType.SSN) {
      return this.utilsService.ssnPattern;
    }
    return this.utilsService.einPattern;
  }

  isValid(): boolean {
    if (this.form.get('control')?.invalid) {
      return false;
    }

    if (this.form.get('control')?.value) {
      return this.getMaskPattern().test(this.form.get('control')?.value);
    }

    return !this.form.get('control')?.hasValidator(Validators.required);
  }

  isEmpty(): boolean {
    return !this.form.get('control')?.value;
  }

  onToggleMaskClick(): void {
    if (this.isFetching() || !this.viewSensitiveLoanData) {
      return;
    }

    if (!this.isReadOnly()) {
      this.mask();
      this.changeDetectorRef.markForCheck();
      return;
    }

    if (this.isEmpty()) {
      this.startEditing();
      this.changeDetectorRef.markForCheck();
      return;
    }

    if (this.revealed() || !this.fortaLoanId()) {
      this.startEditing();
      this.changeDetectorRef.markForCheck();
      return;
    }

    this.fetchSensitiveValue();
  }

  toggleState(): void {
    if (this.isValid() && !this.isEmpty()) {
      this.isReadOnly.set(true);
      this.focusToEdit.set(false);
    } else {
      this.isReadOnly.set(false);
      this.focusToEdit.set(true);
    }
    this.changeDetectorRef.markForCheck();
  }

  maskedValue(): string {
    const value = this.form.get('control')?.value;
    if (value) {
      return this.maskType() === MaskType.SSN
        ? '***-**' + value.substring(value.length - 5, value.length)
        : value.substring(0, 2) + '-*****' + value.substring(value.length - 2);
    }
    return this.maskType() === MaskType.SSN ? '***-**-****' : '**-*******';
  }

  private fetchSensitiveValue(): void {
    const fortaLoanId = this.fortaLoanId();
    const fetch$ = fortaLoanId ? this.fetchSensitiveData(fortaLoanId) : null;
    if (!fetch$) {
      return;
    }

    this.isFetching.set(true);

    fetch$
      .pipe(
        map((data) => this.resolveSensitiveValue(data)),
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((fullValue) => {
        this.isFetching.set(false);
        if (fullValue) {
          this.applyFullValue(fullValue);
        } else if (this.isValid()) {
          this.revealed.set(true);
          this.startEditing();
        } else {
          this.toastMessageService.error(
            'SENSITIVE_DATA_ERROR',
            'Unable to retrieve sensitive data. Please try again.'
          );
        }
        this.changeDetectorRef.markForCheck();
      });
  }

  private fetchSensitiveData(
    fortaLoanId: string
  ): Observable<GetLoanSensitiveSsnQuery | GetLoanSensitiveEinQuery> | null {
    switch (this.maskType()) {
      case MaskType.SSN:
        return this.getLoanSensitiveSsnGQL
          .fetch({ variables: { filter: { fortaLoanId } } })
          .pipe(map(({ data }) => data as GetLoanSensitiveSsnQuery));
      case MaskType.EIN:
        return this.getLoanSensitiveEinGQL
          .fetch({ variables: { filter: { fortaLoanId } } })
          .pipe(map(({ data }) => data as GetLoanSensitiveEinQuery));
      default:
        return null;
    }
  }

  private resolveSensitiveValue(
    data: GetLoanSensitiveSsnQuery | GetLoanSensitiveEinQuery
  ): string | null {
    const borrowerId = this.sensitiveEntityId();
    const guarantorIndex = this.sensitiveGuarantorIndex();

    switch (this.maskType()) {
      case MaskType.SSN: {
        const ssnData = data as GetLoanSensitiveSsnQuery;
        if (borrowerId != null) {
          return (
            ssnData.loan?.borrowers?.find(
              (borrower) => borrower?.borrowerId === borrowerId
            )?.fullSsn ?? null
          );
        }
        if (guarantorIndex != null) {
          return ssnData.loan?.guarantors?.[guarantorIndex]?.fullSsn ?? null;
        }
        return null;
      }
      case MaskType.EIN: {
        const einData = data as GetLoanSensitiveEinQuery;
        if (borrowerId == null) {
          return null;
        }
        return (
          einData.loan?.borrowers?.find(
            (borrower) => borrower?.borrowerId === borrowerId
          )?.fullEinNumber ?? null
        );
      }
      default:
        return null;
    }
  }

  private applyFullValue(value: string): void {
    this.revealed.set(true);
    this.form.get('control')?.setValue(value, { emitEvent: false });
    this.onChange(value);
    this.onTouched();
    this.isReadOnly.set(false);
    this.focusToEdit.set(true);
  }

  private startEditing(): void {
    this.isReadOnly.set(false);
    this.focusToEdit.set(true);
  }

  private mask(): void {
    this.isReadOnly.set(true);
    this.focusToEdit.set(false);
  }
}
